import React from 'react';
import PropTypes from 'prop-types';
import { Label, Input } from 'reactstrap';

import AlertButton from 'ui/Components/Buttons/AlertButton';
import SecondaryButton from 'ui/Components/Buttons/SecondaryButton';
import SizeFitConfirmationModal from './SizeFitConfirmationModal';
import styles from './TableWidgetModal.css';

/**
 * sidebar for widget
 */
export default class Controls extends React.Component {
  static propTypes = {
    addNewSheet: PropTypes.func.isRequired,
    activeSheet: PropTypes.string,
    deleteSheet: PropTypes.func.isRequired,
    handleRowCheckbox: PropTypes.func.isRequired,
    handleDiscardAndQuit: PropTypes.func.isRequired,
    handleSaveAndQuit: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    rowOptions: PropTypes.arrayOf(
      PropTypes.shape({
        allowedValues: PropTypes.arrayOf(
          PropTypes.shape({
            optionDisplayName: PropTypes.string,
            optionValue: PropTypes.string
          })
        ),
        defaultValue: PropTypes.string,
        displayName: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.string
      })
    ),
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        rowData: PropTypes.array,
        rowOptions: PropTypes.shape({
          name: PropTypes.oneOfType([
            PropTypes.shape({
              value: PropTypes.string
            }),
            PropTypes.bool
          ])
        })
      })
    ),
    openInputModal: PropTypes.func.isRequired,
    closeInputModal: PropTypes.func.isRequired,
    selectedCol: PropTypes.number,
    selectedRow: PropTypes.number,
    spliceCol: PropTypes.func,
    spliceRow: PropTypes.func,
    tabs: PropTypes.arrayOf(PropTypes.string),
    exportExcelFile: PropTypes.func.isRequired,
    setNewName: PropTypes.func.isRequired,
    newSheetsName: PropTypes.string.isRequired,
    handleChangeMode: PropTypes.func.isRequired,
    mode: PropTypes.string.isRequired,
    renameSheet: PropTypes.func.isRequired,
    sheetModalType: PropTypes.string
  };

  static defaultProps = {
    rowOptions: [],
    rows: [],
    tabs: [],
    selectedCol: undefined,
    selectedRow: undefined,
    spliceCol: undefined,
    spliceRow: undefined,
    sheetModalType: undefined,
    activeSheet: ''
  };

  /**
   * constructor
   * @param {object} props - properties
   */
  constructor(props) {
    super(props);

    this.renderRowOption = this.renderRowOption.bind(this);
    this.triggerCloseInputModal = this.triggerCloseInputModal.bind(this);
  }

  /**
   * function that closing Input modal and reset setNewName in the states
   */
  triggerCloseInputModal() {
    const { newSheetsName, setNewName, closeInputModal } = this.props;
    if (newSheetsName) {
      setNewName('');
    }
    closeInputModal();
  }

  /**
   * function for rendering rows with inputs
   * @param {object} rowOption - rows data
   * @returns {React.Component} - inputs markup
   */
  renderRowOption(rowOption) {
    const {
      rows,
      handleRowCheckbox,
      handleChangeMode,
      mode,
      selectedRow,
      activeSheet,
      renameSheet,
      setNewName,
      addNewSheet,
      deleteSheet,
      newSheetsName,
      isOpen,
      tabs
    } = this.props;

    const { allowedMods, displayName, name, type } = rowOption;

    switch (type) {
      case 'checkbox': {
        const checked =
          selectedRow !== undefined && rows[selectedRow] && rows[selectedRow].rowOptions[name];
        return (
          <div key={name}>
            <Label for={name}>
              <input
                checked={checked}
                className={styles.checkbox}
                disabled={selectedRow === undefined}
                id={name}
                name={name}
                onChange={handleRowCheckbox}
                type="checkbox"
              />
              {displayName || name}
            </Label>
          </div>
        );
      }

      case 'select': {
        const {
          props: { sheetModalType }
        } = this;
        return (
          <React.Fragment>
            <div>
              {isOpen && (
                <SizeFitConfirmationModal
                  activeSheet={activeSheet}
                  newSheetsName={newSheetsName}
                  renameSheet={renameSheet}
                  setNewName={setNewName}
                  addNewSheet={addNewSheet}
                  sheetModalType={sheetModalType}
                  triggerCloseInputModal={this.triggerCloseInputModal}
                  deleteSheet={deleteSheet}
                  isOpen={isOpen}
                  tabs={tabs}
                  rows={rows}
                />
              )}
            </div>
            <div>
              <Label for="mode">Mode</Label>
              <Input id="mode" onChange={handleChangeMode} type="select" value={mode}>
                {allowedMods.map(option => {
                  const { optionValue, optionDisplayName } = option;

                  return (
                    <option
                      key={`${optionValue}${name}${optionDisplayName}`}
                      value={optionValue}
                    >
                      {optionDisplayName || optionValue}
                    </option>
                  );
                })}
              </Input>
            </div>
          </React.Fragment>
        );
      }

      default:
        // eslint-disable-next-line no-console
        console.error('unsupported row option');

        return null;
    }
  }

  /**
   * react class method render
   * @returns {Node} - markup of component
   */
  render() {
    const {
      handleDiscardAndQuit,
      handleSaveAndQuit,
      selectedRow,
      selectedCol,
      spliceRow,
      spliceCol,
      rowOptions,
      rows,
      tabs,
      exportExcelFile
    } = this.props;

    const rowNotSelected = selectedRow === undefined;
    const colNotSelected = selectedCol === undefined;

    const renderSheetsBlock = () => {
      const tabsBlock = tabs.map((item, index) => (
        <div key={`${item}${index}`}>
          <div>{item}</div>
          <AlertButton
            disabled={false}
            onClick={() => {
              this.props.openInputModal({ mode: 'delete', activeSheet: item });
            }}
          >
            Delete
          </AlertButton>

          <SecondaryButton
            disabled={false}
            onClick={() => {
              this.props.openInputModal({ mode: 'edit', activeSheet: item });
            }}
          >
            Edit Name
          </SecondaryButton>
        </div>
      ));
      return tabsBlock;
    };

    return (
      <div className={styles.controls}>
        <div>
          <h3>Rows</h3>
          <p>{`Selected Row: ${rowNotSelected ? '' : selectedRow + 1}`}</p>
          <button disabled={rowNotSelected} type="button" onClick={spliceRow('up')}>
            Add Row Above
          </button>
          <button disabled={rowNotSelected} type="button" onClick={spliceRow('down')}>
            Add Row Below
          </button>
          <button disabled={rowNotSelected} type="button" onClick={spliceRow('bottom')}>
            Add Row To Bottom
          </button>
          <button
            disabled={rowNotSelected || rows.length < 2}
            type="button"
            onClick={spliceRow('delete')}
          >
            Delete Row
          </button>
          {rowOptions.map(this.renderRowOption)}
        </div>
        <hr />
        <div>
          <h3>Columns</h3>
          <p>{`Selected Column: ${colNotSelected ? '' : selectedCol + 1}`}</p>
          <button disabled={colNotSelected} type="button" onClick={spliceCol('left')}>
            Add Column Before
          </button>
          <button disabled={colNotSelected} type="button" onClick={spliceCol('right')}>
            Add Column After
          </button>
          <button disabled={colNotSelected} type="button" onClick={spliceCol('end')}>
            Add Column To End
          </button>
          <button
            disabled={colNotSelected || rows[0].rowData.length < 2}
            onClick={spliceCol('delete')}
            type="button"
          >
            Delete Column
          </button>
        </div>
        <hr />
        <div>
          <h3>Sheets</h3>
          <SecondaryButton
            disabled={false}
            onClick={() => {
              this.props.openInputModal({ mode: 'add' });
            }}
          >
            Add new sheet
          </SecondaryButton>
          {renderSheetsBlock()}
        </div>
        <hr />
        <div>
          <h3>Save</h3>
          <button type="button" onClick={handleSaveAndQuit}>
            Save and Quit
          </button>
          <button type="button" onClick={handleDiscardAndQuit}>
            Discard and Quit
          </button>
          <button type="button" onClick={exportExcelFile}>
            Export current table to Excel
          </button>
        </div>
      </div>
    );
  }
}
